import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import ClearAllRoundedIcon from "@mui/icons-material/ClearAllRounded";
import { useAuth } from "contexts/AuthContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import linguistApi from "services/linguistApi";
import { NAVBAR_HEIGHT } from "constants/layout";
import { useStateContext } from "contexts/ContextProvider";
import { hasSeenLinguistOnboarding } from "helpers/linguistOnboarding";

// Browser push notification helper
const sendBrowserPushNotification = (title, message) => {
  if (!hasSeenLinguistOnboarding()) return; // first visit: the tour owns the pretext + the request
  if (!("Notification" in window)) {
    return;
  }

  if (Notification.permission === "granted") {
    try {
      new Notification(title, {
        body: message,
        icon: "/favicon.ico",
      });
    } catch (error) {
      console.warn("Failed to send browser push notification:", error.message);
    }
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        try {
          new Notification(title, {
            body: message,
            icon: "/favicon.ico",
          });
        } catch (error) {
          console.warn("Failed to send browser push notification:", error.message);
        }
      }
    });
  }
};

const isVisibleNow = (notification) => {
  if (!notification?.VisibleAt) return true;
  const d = new Date(notification.VisibleAt);
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() <= Date.now();
};

const getNotificationTimestamp = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const parseReadFlag = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return false;
};

const normalizeNotification = (notification) => ({
  ...notification,
  IsRead: parseReadFlag(notification?.IsRead),
});

// Heuristic + metadata-based filter to ensure we only surface linguist-targeted notifications
const isLinguistNotification = (notification) => {
  if (!notification) return false;
  const metadata = notification?.Metadata || {};

  if (metadata?.linguist === true || metadata?.for === "linguist" || metadata?.isLinguistNotification === true) {
    return true;
  }

  const type = String(notification?.Type || "").toLowerCase();
  if (
    type.includes("linguist") ||
    type.includes("assignment") ||
    type.includes("offer") ||
    type.includes("assignment_offer") ||
    // consider service comments as linguist-relevant
    type.includes("service_comment") ||
    type.includes("service comment")
  ) {
    return true;
  }

  
  if (notification?.SourceType === "LinguistAssignmentOffer") return true;

  if (notification?.SourceType === "Service" && String(metadata?.serviceCategory || "").toLowerCase() === "interpretation") {
    return true;
  }

  return false;
};

const sortNotificationsLatestFirst = (items) =>
  [...items].sort((a, b) => {
    const readBucketDifference = Number(parseReadFlag(a?.IsRead)) - Number(parseReadFlag(b?.IsRead));
    if (readBucketDifference !== 0) {
      return readBucketDifference;
    }

    return getNotificationTimestamp(b) - getNotificationTimestamp(a);
  });

const INITIAL_NOTIFICATIONS_LIMIT = 20;
const NEXT_NOTIFICATIONS_LIMIT = 10;

const mergeNotificationsById = (existingNotifications, incomingNotifications) => {
  const mergedById = new Map();

  existingNotifications.forEach((notification) => {
    mergedById.set(Number(notification.id), notification);
  });

  incomingNotifications.forEach((notification) => {
    mergedById.set(Number(notification.id), notification);
  });

  return Array.from(mergedById.values());
};

const LinguistNotifications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar, openDialog, closeDialog } = useStateContext();
  usePageTitle("Notifications");

  const { socket } = useWebSocket();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const loadMoreRef = useRef(null);
  // Raw server offset — advances by the RAW page size so pages never overlap
  // and non-linguist rows can't stall the paging (2026-10-16, C-18).
  const rawOffsetRef = useRef(0);

  // Fetch first page (20)
  const fetchInitialNotifications = useCallback(async () => {
    if (!currentUser?.id) return;

    setLoading(true);
    try {
      const response = await linguistApi.get(`/notifications?limit=${INITIAL_NOTIFICATIONS_LIMIT}&offset=0`);
      const nextNotifications = Array.isArray(response.data)
        ? response.data.map(normalizeNotification)
        : [];
      // Only show linguist-relevant notifications here to keep this UI separate
      const filtered = nextNotifications.filter(isLinguistNotification);
      setNotifications(sortNotificationsLatestFirst(filtered));
      // hasMore keys off the RAW page length — the filtered length can be
      // shorter when a page mixes in non-linguist rows, which used to strand
      // every linguist row past page 1 (2026-10-16, C-18).
      setHasMore(nextNotifications.length === INITIAL_NOTIFICATIONS_LIMIT);
      rawOffsetRef.current = nextNotifications.length;
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
      setHasMore(false);
      rawOffsetRef.current = 0;
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  // Fetch next page (+10)
  const fetchMoreNotifications = useCallback(async () => {
    if (!currentUser?.id || loading || loadingMore || !hasMore) return;

    const offset = rawOffsetRef.current;
    setLoadingMore(true);
    try {
      const response = await linguistApi.get(`/notifications?limit=${NEXT_NOTIFICATIONS_LIMIT}&offset=${offset}`);
      const nextNotifications = Array.isArray(response.data)
        ? response.data.map(normalizeNotification)
        : [];

      const linguistOnly = nextNotifications.filter(isLinguistNotification);

      setNotifications((previousNotifications) => {
        const mergedNotifications = mergeNotificationsById(previousNotifications, linguistOnly);
        return sortNotificationsLatestFirst(mergedNotifications);
      });

      // Advance the raw offset by the raw page size and decide hasMore from
      // the raw page length (2026-10-16, C-18).
      rawOffsetRef.current += nextNotifications.length;
      setHasMore(nextNotifications.length === NEXT_NOTIFICATIONS_LIMIT);
    } catch (error) {
      console.error("Failed to fetch more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentUser?.id, hasMore, loading, loadingMore]);

  // Format relative time (e.g., "2 hours ago")
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  // Format notification type
  const getNotificationTypeLabel = (type) => {
    const typeMap = {
      interpretation_start_in_minutes: "Interpretation Starting",
      interpretation_end_hours_ago: "Interpretation Ended",
      service_deadline_in_days: "Upcoming Deadline",
      service_deadline_today: "Due Today",
      service_deadline_days_ago: "Past Deadline",
      service_assigned: "Service Assigned",
    };
    return typeMap[type] || type;
  };

  // Get color for notification type
  const getTypeColor = (type) => {
    switch (type) {
      case "interpretation_start_in_minutes":
        return "info";
      case "interpretation_end_hours_ago":
        return "success";
      case "service_deadline_in_days":
        return "warning";
      case "service_deadline_today":
        return "warning";
      case "service_deadline_days_ago":
        return "error";
      case "service_assigned":
        return "info";
      default:
        return "default";
    }
  };

  // Mark single notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    const targetId = Number(notificationId);

    let wasAlreadyRead = false;
    setNotifications((prev) =>
      sortNotificationsLatestFirst(prev.map((notif) => {
        if (Number(notif.id) !== targetId) return notif;
        if (parseReadFlag(notif.IsRead)) {
          wasAlreadyRead = true;
          return notif;
        }

        return {
          ...notif,
          IsRead: true,
          ReadAt: notif.ReadAt || new Date().toISOString(),
        };
      }))
    );

    if (wasAlreadyRead) return;

    try {
      await linguistApi.patch(`/notifications/${targetId}/read`);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      showSnackbar(
        "Couldn't mark the notification as read — check your connection and try again.",
        theme,
        "error",
      );
      // Surgical rollback of the optimistic flip — preserves the loaded pages
      // and scroll position (the old full reload reset pagination, C-21).
      setNotifications((prev) =>
        sortNotificationsLatestFirst(prev.map((notif) => {
          if (Number(notif.id) !== targetId) return notif;
          return { ...notif, IsRead: false, ReadAt: null };
        }))
      );
    }
  }, [showSnackbar, theme]);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    setMarkingAsRead(true);
    try {
      await linguistApi.patch("/notifications/read-all");
      setNotifications((prev) =>
        sortNotificationsLatestFirst(prev.map((notif) => ({
          ...notif,
          IsRead: true,
          ReadAt: notif.ReadAt || new Date().toISOString(),
        })))
      );
      showSnackbar("All notifications marked as read.", theme, "success");
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      showSnackbar(
        "Couldn't mark all notifications as read — check your connection and try again.",
        theme,
        "error",
      );
    } finally {
      setMarkingAsRead(false);
      closeDialog();
    }
  }, [closeDialog, showSnackbar, theme]);

  const getNotificationRoute = useCallback((notification) => {
    const metadata = notification?.Metadata || {};

    // DEBUG: Log old notifications to see their structure
    if (!notification?.SourceID) {
      console.log("Old notification structure (no SourceID):", {
        id: notification?.id,
        Type: notification?.Type,
        SourceType: notification?.SourceType,
        SourceID: notification?.SourceID,
        Metadata: metadata,
        Title: notification?.Title,
        Message: notification?.Message,
      });
    }

    if (typeof metadata?.routePath === "string" && metadata.routePath.startsWith("/")) {
      return metadata.routePath;
    }

    if (notification?.SourceType === "Service" && notification?.SourceID) {
      if (metadata?.serviceCategory === "Interpretation") {
        return `/interpretations/${notification.SourceID}`;
      }

      if (metadata?.serviceCategory === "Translation") {
        return `/translations/${notification.SourceID}`;
      }

      if (metadata?.serviceCategory === "Other") {
        return `/otherServices/${notification.SourceID}`;
      }

      return `/services/${notification.SourceID}`;
    }

    return null;
  }, []);

  // Check if linguist has access to the service before navigating
  const checkLinguistAccess = useCallback(async (notification) => {
    if (!notification?.SourceID) return false;

    try {
      // Use the new dedicated access check endpoint
      const response = await linguistApi.get(`/services/${notification.SourceID}/can-access`);
      
      // Return true if linguist has access, false otherwise
      return response.data?.canAccess === true;
    } catch (error) {
      // If we get any error, the linguist doesn't have access
      console.warn('Access check failed:', error);
      return false;
    }
  }, []);

  const handleNotificationCardClick = useCallback(
    async (notification) => {
      const route = getNotificationRoute(notification);
      if (!route) return;

      if (!parseReadFlag(notification?.IsRead)) {
        await handleMarkAsRead(notification.id);
      }

      // Check if linguist has access before navigating
      const hasAccess = await checkLinguistAccess(notification);
      if (!hasAccess) {
        console.warn('Linguist does not have access to this assignment - blocking navigation');
        // Show user-friendly message instead of just blocking silently
        showSnackbar("You don't have access to this assignment", theme, "warning");
        return;
      }

      navigate(route);
    },
    [getNotificationRoute, handleMarkAsRead, navigate, checkLinguistAccess, showSnackbar, theme]
  );

  // Count unread
  const unreadCount = useMemo(
    () => notifications.filter((notification) => !parseReadFlag(notification.IsRead)).length,
    [notifications]
  );

  // Confirm "Mark All as Read" via the global dialog (portal convention). The
  // shared dialog disables its primary button and shows a spinner while the
  // async handler runs, so a double-click can't fire the PATCH twice.
  const handleMarkAllRequest = useCallback(() => {
    openDialog({
      theme,
      title: "Mark All as Read?",
      message: `This will mark all ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""} as read.`,
      primaryAction: "Mark as Read",
      primaryOnClick: () => handleMarkAllAsRead(),
      secondaryAction: "Cancel",
      secondaryOnClick: () => closeDialog(),
    });
  }, [closeDialog, handleMarkAllAsRead, openDialog, theme, unreadCount]);

  // Initial load
  useEffect(() => {
    fetchInitialNotifications();
  }, [fetchInitialNotifications]);

  // Real-time: prepend newly created notifications from socket
  useEffect(() => {
    if (!socket) return;

    const handleNotificationCreated = ({ notification }) => {
      if (!notification) return;
      const incoming = normalizeNotification(notification);
      if (!isVisibleNow(incoming)) return;
      if (!isLinguistNotification(incoming)) return; // ignore member notifications

      // Send browser push notification
      const title = notification?.Title || "New notification";
      const message = notification?.Message || "You have a new notification";
      sendBrowserPushNotification(title, message);

      // NOTE: no auto-navigation on socket arrivals (Amine, 2026-10-16). The
      // old branch keyed on a camelCase path the server never emits, and
      // navigating mid-typing on an arriving offer is hostile UX — the new
      // row is simply prepended below instead.

      setNotifications((prev) => {
        if (prev.some((n) => Number(n.id) === Number(incoming.id))) return prev;
        return sortNotificationsLatestFirst([incoming, ...prev]);
      });
    };

    const handleNotificationUpdated = ({ notification }) => {
      if (!notification) return;
      const incoming = normalizeNotification(notification);
      if (!isLinguistNotification(incoming)) return; // ignore non-linguist updates
      const incomingId = Number(incoming.id);
      if (Number.isNaN(incomingId)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => (Number(item.id) === incomingId ? { ...item, ...incoming } : item))
        )
      );
    };

    const handleNotificationReadAll = ({ userId, readAt }) => {
      if (Number(userId) !== Number(currentUser?.id)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => ({
            ...item,
            IsRead: true,
            ReadAt: item.ReadAt || readAt || new Date().toISOString(),
          }))
        )
      );
    };

    socket.on("notification:created", handleNotificationCreated);
    socket.on("notification:updated", handleNotificationUpdated);
    socket.on("notification:read-all", handleNotificationReadAll);
    return () => {
      socket.off("notification:created", handleNotificationCreated);
      socket.off("notification:updated", handleNotificationUpdated);
      socket.off("notification:read-all", handleNotificationReadAll);
    };
  }, [socket, currentUser?.id]);

  useEffect(() => {
    if (loading || !hasMore) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !loadingMore) {
          fetchMoreNotifications();
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchMoreNotifications, hasMore, loading, loadingMore]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const totalNotifications = notifications.length;

  return (
    <Box
      sx={{
        p: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        maxWidth: "900px",
        mx: "auto",
        minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box>
            <Typography variant="body2" color="textSecondary">
              {totalNotifications === 0
                ? "No notifications"
                : unreadCount > 0
                ? `${unreadCount} unread of ${totalNotifications} total`
                : `${totalNotifications} notification${totalNotifications !== 1 ? "s" : ""}`}
            </Typography>
          </Box>

          {totalNotifications > 0 && (
            <Button
              variant="outlined"
              startIcon={<ClearAllRoundedIcon />}
              onClick={handleMarkAllRequest}
              disabled={unreadCount === 0 || markingAsRead}
              sx={{
                alignSelf: isMobile ? "stretch" : "auto",
              }}
            >
              Mark All as Read
            </Button>
          )}
        </Stack>
      </Box>

      {/* Empty State */}
      {totalNotifications === 0 && (
        <Card
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.primary.border2}`,
          }}
        >
          <MarkEmailReadRoundedIcon
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              mb: 2,
              opacity: 0.5,
            }}
          />
          <Typography variant="h6" color="textSecondary">
            All caught up!
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            You don't have any notifications at the moment.
          </Typography>
        </Card>
      )}

      {totalNotifications > 0 && (
        <Box>
          <Stack spacing={1}>
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onMarkAsRead={() => handleMarkAsRead(notification.id)}
                onOpenProfile={() => handleNotificationCardClick(notification)}
                formatRelativeTime={formatRelativeTime}
                getNotificationTypeLabel={getNotificationTypeLabel}
                getTypeColor={getTypeColor}
                theme={theme}
                isUnread={!parseReadFlag(notification.IsRead)}
              />
            ))}
          </Stack>

          {loadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {hasMore && <Box ref={loadMoreRef} sx={{ height: 1 }} />}
        </Box>
      )}
    </Box>
  );
};

// Individual Notification Card Component
const NotificationCard = ({
  notification,
  onMarkAsRead,
  onOpenProfile,
  formatRelativeTime,
  getNotificationTypeLabel,
  getTypeColor,
  theme,
  isUnread,
}) => {
  const [hovering, setHovering] = React.useState(false);

  return (
    <Card
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={onOpenProfile}
      sx={{
        p: 2,
        backgroundColor: isUnread
          ? theme.palette.mode === "dark"
            ? "rgba(33, 150, 243, 0.08)"
            : "rgba(33, 150, 243, 0.04)"
          : theme.palette.background.paper,
        border: `1px solid ${
          isUnread ? theme.palette.primary.main : theme.palette.primary.border2
        }`,
        borderLeftWidth: isUnread ? "4px" : "1px",
        borderLeftColor: isUnread ? theme.palette.primary.main : "transparent",
        transition: "all 0.2s ease",
        cursor: onOpenProfile ? "pointer" : "default",
        "&:hover": {
          boxShadow: theme.shadows[2],
          backgroundColor: isUnread
            ? theme.palette.mode === "dark"
              ? "rgba(33, 150, 243, 0.12)"
              : "rgba(33, 150, 243, 0.06)"
            : theme.palette.action.hover,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        {/* Main Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
            <Chip
              label={getNotificationTypeLabel(notification.Type)}
              size="small"
              color={getTypeColor(notification.Type)}
              variant="outlined"
              sx={{ height: 24 }}
            />
            {isUnread && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                }}
              />
            )}
          </Stack>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: isUnread ? 600 : 500,
              mb: 0.5,
              wordBreak: "break-word",
            }}
          >
            {notification.Title}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 1,
              wordBreak: "break-word",
              lineHeight: 1.5,
            }}
          >
            {notification.Message}
          </Typography>

          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: "block",
              fontSize: "0.8rem",
            }}
          >
            {formatRelativeTime(notification.VisibleAt || notification.createdAt)}
            {notification.ReadAt && (
              <>
                {" • "}
                <span style={{ fontStyle: "italic" }}>
                  Read {formatRelativeTime(notification.ReadAt)}
                </span>
              </>
            )}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            opacity: hovering ? 1 : 0.6,
            transition: "opacity 0.2s",
          }}
        >
          {isUnread && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onMarkAsRead();
              }}
              title="Mark as read"
              aria-label="Mark as read"
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MarkEmailReadRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Metadata (Optional: Show Source Info if Available) */}
      {notification.Metadata && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: "block",
              fontSize: "0.75rem",
              opacity: 0.7,
            }}
          >
            {notification.SourceType && `Type: ${notification.SourceType}`}
            {notification.SourceID && ` • ID: ${notification.SourceID}`}
          </Typography>
        </>
      )}
    </Card>
  );
};

export default LinguistNotifications;
